<script context="module">
  import { paymentErrorsStore } from "../../store.js";
  import pdfMake from "pdfmake/build/pdfmake";
  import pdfFonts from "pdfmake/build/vfs_fonts";
  import moment from "moment";

  pdfMake.addVirtualFileSystem(pdfFonts);

  export const PAYMENT_SESSION_ID_QUERY_KEY = "orderId";

  export const Providers = {
    CARD: "STRIPE",
    PWT: "TOONIE",
    REGISTRATION: "CARD_REGISTRATION",
    STREAM: "CRYPTO",
  };

  export const PaymentStatus = {
    ACCEPTED: "ACCEPTED",
    APPROVED: "APPROVED",
    COMPLETED: "COMPLETED",
    CREATED: "CREATED",
    INITIATED: "INITIATED",
    PENDING: "PENDING",
    SUCCEEDED: "SUCCEEDED",
    REJECTED: "REJECTED",
  };

  // helper that validates options object and returns true if something is invalid
  export const checkInvalidOptions = (options) => {
    // options should be an object and should be not null
    if (typeof options !== "object" || options === null) {
      console.warn("options must be an object");
      return true;
    }

    // options.baseUrl should be a string
    if (options.baseUrl && typeof options.baseUrl !== "string") {
      console.warn("baseUrl must be a string");
      return true;
    }

    // options.createPaymentSession is required
    if (!options.createPaymentSession) {
      console.warn("createPaymentSession is required");
      return true;
    }

    // options.createPaymentSession should be a function type
    if (typeof options.createPaymentSession !== "function") {
      console.warn("createPaymentSession must be a function");
      return true;
    }

    // options.failurePaymentCallback is provided it should be a function
    if (
      options.failurePaymentCallback &&
      typeof options.failurePaymentCallback !== "function"
    ) {
      console.warn("failurePaymentCallback must be a function");
      return true;
    }

    // options.successPaymentCallback is provided it should be a function
    if (
      options.successPaymentCallback &&
      typeof options.successPaymentCallback !== "function"
    ) {
      console.warn("successPaymentCallback must be a function");
      return true;
    }

    // if options.genericErrorCallback is provided it should be a function
    if (
      options.genericErrorCallback &&
      typeof options.genericErrorCallback !== "function"
    ) {
      console.warn("genericErrorCallback must be a function");
      return true;
    }

    // if options.genericErrorCallback is provided it should be a function
    if (options.onModalClose && typeof options.onModalClose !== "function") {
      console.warn("onModalClose must be a function");
      return true;
    }

    return false;
  };

  export const formatAmountToDisplay = (amount, currency) => {
    if (!(amount && currency)) return "---";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  export const getFirstTwoLetters = (word) => {
    if (!word) return "";
    // Check if the word is a string and has at least two characters
    if (typeof word === "string" && word.length >= 2) {
      return word.substring(0, 2).toUpperCase();
    } else {
      // Handle cases where the word is too short or not a string
      return word.toUpperCase();
    }
  };

  export const calculateMaxBirthDateFromNow = () => {
    // Calculate the date 18 years ago from today
    const today = new Date();
    const year = today.getFullYear() - 18;
    const month = today.getMonth() + 1; // Months are zero-indexed, so add 1
    const day = today.getDate();

    // Format the date to YYYY-MM-DD for the input field
    return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  };

  export const setError = (error) => {
    // setting it to null because if the same error is set twice, the second time it doesn't trigger the subscribers
    paymentErrorsStore.set(null);

    switch (error) {
      case "NO_PAYMENT_SESSION_ID":
        paymentErrorsStore.set("Missing Payment Session Id");
        break;
      case "NO_CARD_PAYMENT_PARAMS":
        paymentErrorsStore.set(
          "Error on loading payment data: missing clientSecret or paymentId",
        );
        break;
      case "NO_STREAM_PAYMENT_PARAMS":
        paymentErrorsStore.set(
          "Error on loading payment data: missing intentId",
        );
        break;
      case "STREAM_UNEXPECTED_ERROR":
        paymentErrorsStore.set(
          "There was an error loading data for this stream",
        );
        break;
      case "REGISTRATION_UNEXPECTED_ERROR":
        paymentErrorsStore.set(
          "There was an error loading data for this stream",
        );
        break;
      case "UNEXPECTED_ERROR":
        paymentErrorsStore.set("Unexpected error during the payment");
        break;
      case "PAYMENT_INTENT_NOT_FOUND":
        paymentErrorsStore.set("Session id not found in our system");
        break;
      default:
        paymentErrorsStore.set("Unexpected error");
        break;
    }
  };

  export const getRawPaymentStatusData = async (options, sessionId) => {
    const baseUrl = options?.baseUrl ?? "https://api.toonieglobal.com";

    const res = await fetch(
      `${baseUrl}/acquiring/v1/payment/${sessionId}/approve`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!res.ok) {
      throw new Error(`${res.status} while approving payment`, {
        cause: await res.text(),
      });
    }
    return await res.json();
  };

  export const isPaymentApproved = (paymentStatus) => {
    const provider = paymentStatus?.provider;

    if (!provider) return false;

    return (
      provider.status === PaymentStatus.APPROVED ||
      provider.status === PaymentStatus.COMPLETED
    );
  };

  export const completeOrderAndRedirect = async ({
    sessionId,
    paymentStatus,
    paymentData,
    redirectDelay,
    options,
    sessionSuccessUrl,
  }) => {
    const provider = paymentStatus?.provider;
    if (options.updatePayment) {
      await options.updatePayment(sessionId, provider.status);
    }

    if (options.successPaymentCallback) {
      options.successPaymentCallback(paymentStatus, paymentData);
    }
    paymentErrorsStore.set(undefined);

    setTimeout(
      () => (location.href = sessionSuccessUrl ?? successUrl),
      redirectDelay,
    );
  };

  export const downloadReceipt = (paymentData) => {
    const fileName = `receipt-${paymentData.paymentSessionId}`;
    const pdf = pdfMake.createPdf({
      info: {
        title: "receipt",
      },
      content: [
        // Logo
        {
          svg:
            '<svg fill="none" width="200" height="65" viewBox="0 0 230 65" xmlns="http://www.w3.org/2000/svg"> <path\n' +
            '      d="M0 19.7618C0 17.7937 1.5948 16.2939 3.46784 16.2939H8.42869V7.86523C8.42869 5.71394 10.1117 4.11914 12.1748 4.11914C14.3261 4.11914 15.9209 5.71394 15.9209 7.86523V16.2939H28.2857C30.1587 16.2939 31.7535 17.7937 31.7535 19.7618C31.7535 21.6348 30.1587 23.2296 28.2857 23.2296H15.9209V44.3963C15.9209 53.1033 20.8817 57.7859 28.2857 57.7859C30.1587 57.7859 31.7535 59.3807 31.7535 61.2537C31.7535 63.2218 30.1587 64.8098 28.2857 64.8098C16.6742 64.8098 8.42869 56.9444 8.42869 44.3963V23.2296H3.46784C1.5948 23.2296 0 21.6348 0 19.7618Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "    <path\n" +
            '      d="M28.9434 40.1821C28.9434 26.3243 39.6183 15.3643 53.6662 15.3643C67.714 15.3643 78.294 26.3243 78.294 40.1821C78.294 54.0399 67.714 64.9999 53.6662 64.9999C39.6183 64.9999 28.9434 54.0399 28.9434 40.1821ZM70.7068 40.1821C70.7068 30.2536 63.3096 22.2932 53.6594 22.2932C44.0159 22.2932 36.5238 30.2536 36.5238 40.1821C36.5238 50.0156 44.0159 58.071 53.6594 58.071C63.3096 58.0642 70.7068 50.0156 70.7068 40.1821Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "    <path\n" +
            '      d="M78.5723 40.1821C78.5723 26.3243 89.2473 15.3643 103.295 15.3643C117.343 15.3643 127.923 26.3243 127.923 40.1821C127.923 54.0399 117.343 64.9999 103.295 64.9999C89.254 64.9999 78.5723 54.0399 78.5723 40.1821ZM120.342 40.1821C120.342 30.2536 112.945 22.2932 103.295 22.2932C93.6516 22.2932 86.1594 30.2536 86.1594 40.1821C86.1594 50.0156 93.6516 58.071 103.295 58.071C112.945 58.0642 120.342 50.0156 120.342 40.1821Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "    <path\n" +
            '      d="M130.367 36.9929C130.367 23.4134 139.454 15.3579 150.876 15.3579C162.392 15.3579 171.479 23.4134 171.479 36.9929V60.9692C171.479 63.1205 169.796 64.8103 167.733 64.8103C165.582 64.8103 163.987 63.1273 163.987 60.9692V36.8097C163.987 27.2544 158.178 22.2936 150.876 22.2936C143.662 22.2936 137.859 27.2544 137.859 36.8097V60.9692C137.859 63.1205 136.176 64.8103 134.113 64.8103C131.962 64.8103 130.367 63.1273 130.367 60.9692V36.9929Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "    <path\n" +
            '      d="M175.035 4.68261C175.035 2.06306 177.098 0 179.718 0C182.249 0 184.4 2.06306 184.4 4.68261C184.4 7.21393 182.249 9.36521 179.718 9.36521C177.098 9.36521 175.035 7.21393 175.035 4.68261ZM175.972 19.2937C175.972 17.2306 177.566 15.5476 179.718 15.5476C181.781 15.5476 183.464 17.2306 183.464 19.2937V60.9689C183.464 63.1202 181.781 64.81 179.718 64.81C177.566 64.81 175.972 63.127 175.972 60.9689V19.2937Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "    <path\n" +
            '      d="M185.805 40.1821C185.805 26.3243 196.48 15.3643 210.527 15.3643C217.361 15.3643 223.076 17.9838 226.727 21.3566C227.568 22.1031 228.131 23.1347 228.131 24.2612C228.131 25.1977 227.853 26.0392 226.917 27.0708L210.249 47.016C209.503 47.8575 208.654 48.2307 207.63 48.2307C205.567 48.2307 204.074 46.8259 204.074 44.8579C204.074 44.0164 204.264 43.1749 204.915 42.5166L219.526 25.286C217.09 23.2229 213.629 22.2864 210.534 22.2864C200.979 22.2864 193.399 30.1518 193.399 40.1753C193.399 50.1038 200.891 58.0642 210.534 58.0642C215.875 58.0642 220.09 55.7229 222.987 52.4451C224.019 51.5086 224.86 50.7621 226.17 50.7621C228.321 50.7621 229.916 52.2618 229.916 54.3181C229.916 55.0646 229.448 56.0962 228.79 56.8494C224.854 61.532 218.393 64.9999 210.527 64.9999C196.575 64.9999 185.805 54.0399 185.805 40.1821Z"\n' +
            '      fill="#f4b31d"\n' +
            "    />\n" +
            "</svg>",
          width: 150,
          alignment: "left",
          margin: [0, 0, 0, 10],
        },

        // Receipt title
        {
          text: "Payment receipt",
          style: "title",
          margin: [0, 10, 0, 30],
        },

        // Intro text
        {
          text: "This is the payment receipt for your payment to:",
          margin: [0, 0, 0, 10],
        },

        // Merchant details
        {
          text: [
            { text: `${paymentData.merchantDisplayName}\n`, bold: true },
            `${paymentData.merchantAddress.addressLine1}
          ${paymentData.merchantAddress.city}
          ${paymentData.merchantAddress.postalCode}
          ${paymentData.merchantAddress.state ? `${paymentData.merchantAddress.state}, ` : ""} ${paymentData.merchantAddress.country}`,
          ],
          margin: [0, 0, 0, 20],
        },

        // Thank you message
        {
          text: "Thank you for your payment!",
          style: "thankYou",
          margin: [0, 60, 0, 40],
        },

        // Payment details header
        { text: "Payment details", bold: true, margin: [0, 0, 0, 8] },

        // Payment details
        {
          table: {
            widths: ["auto", "*"],
            body: [
              ["Date:", moment(paymentData.lastUpdated).format("DD/MM/YYYY")],
              ["Receipt n°:", paymentData.paymentSessionId],
              ["Status:", paymentData.status],
              ["Description:", paymentData.reason],
            ],
          },
          layout: {
            defaultBorder: false,
            paddingBottom: () => 8,
          },
          margin: [0, 0, 0, 30],
        },

        // Horizontal line
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 1,
              color: "#133d59",
            },
          ],
          margin: [0, 30, 0, 30],
        },

        // Amount paid
        {
          columns: [
            { width: "*", text: "Amount paid:" },
            {
              width: "auto",
              text: `${formatAmountToDisplay(paymentData.amount, paymentData.currency)}`,
              style: "amount",
            },
          ],
        },
      ],
      styles: {
        title: {
          fontSize: 24,
          bold: true,
          alignment: "center",
        },
        thankYou: {
          margin: [0, 30, 0, 30],
          alignment: "center",
        },
        amount: {
          fontSize: 16,
          bold: true,
        },
      },
      defaultStyle: {
        font: "Roboto",
        fontSize: 14,
        color: "#133d59",
      },
      pageMargins: [40, 60, 40, 60],
    });

    pdf.download(fileName);
  };
</script>
